/**
 * ProfileSelector Component
 *
 * Horizontal tab-based selector for risk profiles with inline editing,
 * create/delete operations, and default badge display.
 *
 * Design reference: Onboarding ProfileList component, adapted for
 * schema-backed RiskProfile types (id instead of tempId).
 */

// 1. External dependencies
import React, { useState } from 'react';
import { Pencil, Check, X, Star } from 'lucide-react';

// 2. Workspace packages
import { Button, Popover, PopoverTrigger, PopoverContent, Tabs, Tab, Input } from '@enfomentis/ui';

// 3. Internal absolute imports
import type { RiskProfile } from '@/types/schema/AccountRisk';

/**
 * ProfileSelector props
 */
interface ProfileSelectorProps {
  profiles: RiskProfile[];
  selectedProfileId: string | null;
  defaultProfileId: string | null;
  onSelectProfile: (profileId: string) => void;
  onAddProfile: () => void;
  onDeleteProfile: (profileId: string) => void;
  onRenameProfile: (profileId: string, newName: string) => void;
  onSetDefault: (profileId: string) => void;
  canDelete: (profileId: string) => boolean;
}

/**
 * ProfileSelector component
 */
export const ProfileSelector: React.FC<ProfileSelectorProps> = ({
  profiles,
  selectedProfileId,
  defaultProfileId,
  onSelectProfile,
  onAddProfile,
  onDeleteProfile,
  onRenameProfile,
  onSetDefault,
  canDelete,
}) => {
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmingDefaultId, setConfirmingDefaultId] = useState<string | null>(null);

  const handleStartEdit = (profile: RiskProfile): void => {
    setEditingProfileId(profile.id);
    setEditingName(profile.name);
  };

  const isNameDuplicate = (name: string, excludeProfileId: string): boolean => {
    const normalized = name.trim().toLowerCase();
    if (!normalized) return false;
    return profiles.some(
      p => p.id !== excludeProfileId && p.name.toLowerCase() === normalized
    );
  };

  const handleSaveName = (profileId: string): void => {
    const trimmed = editingName.trim();
    if (!trimmed) {
      handleCancelEdit();
      return;
    }
    if (isNameDuplicate(trimmed, profileId)) return;
    onRenameProfile(profileId, trimmed);
    setEditingProfileId(null);
    setEditingName('');
  };

  const handleCancelEdit = (): void => {
    setEditingProfileId(null);
    setEditingName('');
  };

  return (
    <div className="w-full overflow-x-auto scrollbar-hide">
      <Tabs
        aria-label="Risk profiles"
        selectedKey={selectedProfileId || ''}
        onSelectionChange={(key) => {
          if (key === '_add_profile') {
            onAddProfile();
          } else {
            onSelectProfile(key as string);
          }
        }}
        variant="underlined"
        color="primary"
        fullWidth={false}
      >
        {profiles.map((profile) => (
          <Tab
            key={profile.id}
            title={
              <div className="flex items-center gap-2 w-auto">
                {/* Profile color indicator */}
                {profile.color && (
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: profile.color }}
                  />
                )}

                {/* Profile name - editable inline */}
                {editingProfileId === profile.id ? (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Input
                      size="sm"
                      value={editingName}
                      isInvalid={isNameDuplicate(editingName, profile.id)}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveName(profile.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      autoFocus
                      className="w-32"
                    />
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="success"
                      onPress={() => handleSaveName(profile.id)}
                      aria-label="Confirm rename"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={handleCancelEdit}
                      aria-label="Cancel rename"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <span>{profile.name}</span>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => handleStartEdit(profile)}
                      aria-label="Edit profile name"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}

                {/* Default star indicator / set-as-default control */}
                {editingProfileId !== profile.id && (
                  profile.id === defaultProfileId ? (
                    <Star className="w-3.5 h-3.5 text-warning shrink-0" fill="currentColor" />
                  ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Popover
                        placement="bottom"
                        isOpen={confirmingDefaultId === profile.id}
                        onOpenChange={(open) => {
                          if (!open) setConfirmingDefaultId(null);
                        }}
                      >
                        <PopoverTrigger>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => setConfirmingDefaultId(profile.id)}
                            aria-label={`Set ${profile.name} as default`}
                          >
                            <Star className="w-3.5 h-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                          <div className="flex flex-col gap-2 p-2">
                            <p className="text-sm">Set as default?</p>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                color="primary"
                                onPress={() => {
                                  onSetDefault(profile.id);
                                  setConfirmingDefaultId(null);
                                }}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="light"
                                onPress={() => setConfirmingDefaultId(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )
                )}

                {/* Delete button */}
                {canDelete(profile.id) && editingProfileId !== profile.id && (
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => onDeleteProfile(profile.id)}
                    aria-label="Delete profile"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            }
          />
        ))}

        {/* Add Profile Tab */}
        <Tab
          key="_add_profile"
          title={
            <div className="flex items-center gap-2 text-primary">
              <span>+</span>
              <span>Add Profile</span>
            </div>
          }
        />
      </Tabs>
    </div>
  );
};
